import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import { BADGE_ROUGE, BADGE_VERT, PointJugement } from "./coloriage-points";
import {
  type ActionSurDemande,
  demandeActive,
  messageDEchec,
  useActionSurDemande,
} from "./coloriage-requetes";
import {
  depuis,
  detailDemande,
  LIBELLE_ETAT_DEMANDE,
  repriseEnClair,
  resumeDemande,
  travailEnClair,
} from "./coloriage-textes";
import type { Agent, Demande } from "./contract";
import { compter } from "./format";

/**
 * Ce que l'accueil et la grille partagent depuis le ticket 3 (#152) : la carte de l'agent du
 * PC, la table des demandes et le message qui suit une ecriture.
 *
 * Textes et disposition : `ColoriageAccueil.dc.html` (carte de l'agent, table des demandes)
 * et `ColoriageEtats.dc.html` (agent eteint, ComfyUI occupe ou injoignable, demande en
 * echec). Les encadres reprennent les tokens de jugement, deja mesures sur une carte (section
 * 5 de `theme.css`) : ambre pour ce qui attend, rouge pour ce qui a echoue.
 */

/** La commande qui lance l'agent sur le PC (`scripts/coloriage-agent.ps1`, ticket 3). */
export const COMMANDE_AGENT = "./scripts/coloriage-agent.ps1";

export const ALERTE_GRISE = "rounded-md border border-border bg-muted px-3.5 py-2.5 text-sm";
export const ALERTE_AMBRE =
  "rounded-md border border-jugement-defaut/40 bg-jugement-defaut/15 px-3.5 py-2.5 text-sm text-jugement-defaut";
export const ALERTE_ROUGE =
  "rounded-md border border-jugement-jeter/40 bg-jugement-jeter/15 px-3.5 py-2.5 text-sm text-jugement-jeter";

/** Une commande a taper sur le PC, sur un fond de carte pour ressortir d'un encadre gris. */
function Commande({ children }: { children: string }) {
  return (
    <code className="rounded border border-border bg-card px-1.5 py-0.5 text-foreground">
      {children}
    </code>
  );
}

// ---------------------------------------------------------------------------------------
// Le message qui suit une ecriture.

/** Ce qu'une ecriture a donne : une phrase d'etat, ou le refus du backend. */
export type Retour = { readonly ton: "ok" | "echec"; readonly texte: string };

/**
 * Le message qui suit une ecriture. Une reussite prend le focus (`tabIndex={-1}`) : le bouton
 * presse s'eteint ou disparait souvent avec elle (cases decochees, demande annulee), et sans
 * cible le focus du clavier retomberait sur `body`. Un refus est une alerte, et le focus
 * reste sur le bouton, pour reessayer.
 */
export function MessageDeRetour({ retour }: { retour: Retour | null }) {
  const phrase = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (retour?.ton === "ok") phrase.current?.focus();
  }, [retour]);
  if (retour === null) return null;
  if (retour.ton === "echec") {
    return (
      <p className="text-sm text-destructive" role="alert">
        {retour.texte}
      </p>
    );
  }
  return (
    <p
      className="rounded-sm text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      ref={phrase}
      role="status"
      tabIndex={-1}
    >
      {retour.texte}
    </p>
  );
}

// ---------------------------------------------------------------------------------------
// L'agent du PC.

/** La version de l'agent : `coloring 0.1.0`. */
function versionEnClair(version: string): string {
  return version.startsWith("coloring") ? version : `coloring ${version}`;
}

/**
 * La carte de l agent : en ligne (vu il y a moins de 90 s, calcule par le serveur), hors
 * ligne depuis tant de temps, ou jamais annonce ; puis, en ligne, l'etat de ComfyUI et ce que
 * l'agent rend.
 */
export function CarteAgent({
  agent,
  demandes,
  maintenant,
}: {
  agent: Agent | null;
  demandes: readonly Demande[];
  maintenant: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Agent du PC</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <EtatDeLAgent agent={agent} demandes={demandes} maintenant={maintenant} />
      </CardContent>
    </Card>
  );
}

function EtatDeLAgent({
  agent,
  demandes,
  maintenant,
}: {
  agent: Agent | null;
  demandes: readonly Demande[];
  maintenant: number;
}) {
  const actives = demandes.filter(demandeActive).length;
  if (agent === null || !agent.en_ligne) {
    return (
      <div className={ALERTE_GRISE}>
        <p className="flex items-center gap-2.5">
          <PointJugement jugement={null} />
          <strong className="font-semibold">
            {agent === null ? (
              "L'agent du PC ne s'est encore jamais annoncé"
            ) : (
              // La duree d'un seul tenant : « 2 h 14 » ne se coupe pas en fin de ligne.
              <>
                Agent du PC hors ligne depuis{" "}
                <span className="whitespace-nowrap">{depuis(agent.vu_le, maintenant)}</span>
              </>
            )}
          </strong>
        </p>
        <p className="mt-1.5">
          {actives > 0
            ? `${compter(actives, "demande attend", "demandes attendent")}. ${
                actives > 1 ? "Elles partiront" : "Elle partira"
              } dès que l'agent sera lancé sur le PC : `
            : "Il se lance sur le PC quand tu veux produire : "}
          <Commande>{COMMANDE_AGENT}</Commande>
        </p>
      </div>
    );
  }

  // La demande que l'agent dit tenir, si elle tourne encore d'apres la console.
  const courante = demandes.find(
    (demande) => demande.id === agent.demande && demande.etat === "en_cours",
  );
  const attend = demandes.some((demande) => demande.etat === "en_attente");
  return (
    <>
      <p className="flex flex-wrap items-center gap-x-2.5">
        <PointJugement jugement="bonne" />
        <strong className="font-semibold">Connecté</strong>
        <span className="text-muted-foreground">il y a {depuis(agent.vu_le, maintenant)}</span>
      </p>
      <EtatDeComfy agent={agent} attend={attend} courante={courante} />
      {agent.version === null ? null : (
        <p className="text-xs text-muted-foreground">{versionEnClair(agent.version)}</p>
      )}
      <p className="text-xs text-muted-foreground">
        Il se lance sur le PC quand tu veux produire : <Commande>{COMMANDE_AGENT}</Commande>
      </p>
    </>
  );
}

/**
 * ComfyUI, vu par l'agent. L'agent ne prend une demande que si ComfyUI repond et que sa file
 * est vide : une file pleine qu'il ne remplit pas lui-meme vient donc d'un rendu lance hors de
 * la console.
 */
function EtatDeComfy({
  agent,
  courante,
  attend,
}: {
  agent: Agent;
  courante: Demande | undefined;
  attend: boolean;
}) {
  if (agent.comfyui === "injoignable") {
    return (
      <p className={ALERTE_AMBRE}>
        <strong className="font-semibold">ComfyUI ne répond pas sur le PC.</strong> Lance Comfy
        Desktop : l'agent le verra à son prochain signe de vie, dans 5 s.
      </p>
    );
  }
  if (agent.comfyui === "occupe" && courante === undefined) {
    const file =
      agent.file === null
        ? ""
        : ` (${compter(agent.file, "rendu lancé", "rendus lancés")} hors de la console)`;
    return attend ? (
      <p className={ALERTE_AMBRE}>
        <strong className="font-semibold">
          L'agent attend : la file de ComfyUI n'est pas vide
        </strong>
        {file}. Il prendra la demande quand elle sera vide.
      </p>
    ) : (
      <p className={ALERTE_AMBRE}>
        <strong className="font-semibold">La file de ComfyUI n'est pas vide</strong>
        {file}. L'agent ne prendra une demande que quand elle sera vide.
      </p>
    );
  }
  const suite =
    courante !== undefined
      ? `, ${travailEnClair(courante)}`
      : attend
        ? " : l'agent prend la demande à son prochain signe de vie"
        : ", rien à rendre";
  return <p>ComfyUI joignable{suite}</p>;
}

// ---------------------------------------------------------------------------------------
// Les demandes.

const BADGE = "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold";
const BADGE_GRIS = "bg-muted text-muted-foreground";

const TON_ETAT: Readonly<Record<Demande["etat"], string>> = {
  en_attente: BADGE_GRIS,
  en_cours: "bg-foreground text-background",
  faite: BADGE_VERT,
  echouee: BADGE_ROUGE,
  annulee: BADGE_GRIS,
};

/**
 * Le bouton d'une demande : annuler ce qui n'est pas fini, relancer ce qui s'est arrete. Le
 * backend refuse de relancer une demande d'un cahier clos (la cloture annule ce qui attendait) :
 * pas de bouton qui ne mene qu'a un refus.
 */
function actionPossible(
  etat: Demande["etat"],
  cahierClos: boolean,
): { readonly action: ActionSurDemande["action"]; readonly libelle: string } | null {
  if (etat === "en_attente" || etat === "en_cours")
    return { action: "annuler", libelle: "Annuler" };
  if ((etat === "echouee" || etat === "annulee") && !cahierClos) {
    return { action: "relancer", libelle: "Relancer la demande" };
  }
  return null;
}

const AUCUN_CAHIER_CLOS: ReadonlySet<number> = new Set();

/**
 * La table des demandes, sur le modele de la maquette : ce qui est demande, le cahier (a
 * l'accueil seulement), l'etat et son bouton. `annoncer` recoit le message d'une ecriture :
 * c'est la page qui l'affiche, a un endroit qui reste quand la ligne disparait.
 */
export function TableDemandes({
  demandes,
  maintenant,
  avecCahier,
  titreId,
  annoncer,
  cahiersClos = AUCUN_CAHIER_CLOS,
}: {
  demandes: readonly Demande[];
  maintenant: number;
  avecCahier: boolean;
  /** L'identifiant du titre qui nomme la table. */
  titreId: string;
  annoncer: (retour: Retour) => void;
  /** Les cahiers clos : leurs demandes arretees ne se relancent plus. */
  cahiersClos?: ReadonlySet<number> | undefined;
}) {
  const action = useActionSurDemande();
  // Un verrou synchrone : un double clic arrive avant que `isPending` ne soit rendu.
  const envoi = useRef(false);

  const agir = (demande: Demande, quoi: ActionSurDemande["action"]): void => {
    if (envoi.current) return;
    envoi.current = true;
    action.mutate(
      { id: demande.id, action: quoi },
      {
        onSuccess: (reponse) =>
          annoncer({
            ton: "ok",
            texte: `${quoi === "annuler" ? "Demande annulée" : "Demande relancée"} : ${resumeDemande(
              reponse.demande,
            )}.`,
          }),
        onError: (error) => annoncer({ ton: "echec", texte: messageDEchec(error) }),
        onSettled: () => {
          envoi.current = false;
        },
      },
    );
  };

  const entete = "px-3 pb-2 font-semibold";
  return (
    // Sur telephone, la table defile a l'horizontale plutot que d'ecraser l'etat.
    <div className="overflow-x-auto">
      <table
        aria-labelledby={titreId}
        className={cn(
          "w-full border-collapse text-sm",
          avecCahier ? "min-w-[36rem]" : "min-w-[28rem]",
        )}
      >
        <thead>
          <tr className="text-left text-xs font-semibold text-muted-foreground">
            <th className={entete} scope="col">
              Demande
            </th>
            {avecCahier ? (
              <th className={entete} scope="col">
                Cahier
              </th>
            ) : null}
            <th className={cn(entete, "w-[15rem]")} scope="col">
              État
            </th>
          </tr>
        </thead>
        <tbody>
          {demandes.map((demande) => (
            <LigneDemande
              agir={agir}
              avecCahier={avecCahier}
              cahierClos={cahiersClos.has(demande.cahier.id)}
              demande={demande}
              key={demande.id}
              maintenant={maintenant}
              occupe={action.isPending}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LigneDemande({
  demande,
  maintenant,
  avecCahier,
  cahierClos,
  occupe,
  agir,
}: {
  demande: Demande;
  maintenant: number;
  avecCahier: boolean;
  cahierClos: boolean;
  occupe: boolean;
  agir: (demande: Demande, quoi: ActionSurDemande["action"]) => void;
}) {
  const cellule = "border-t border-border px-3 py-2.5 align-top";
  const resumeId = `coloriage-demande-${demande.id}`;
  const bouton = actionPossible(demande.etat, cahierClos);
  return (
    <tr>
      <td className={cellule}>
        <span className="font-medium" id={resumeId}>
          {resumeDemande(demande)}
        </span>
        <span className="block text-xs text-muted-foreground">{detailDemande(demande)}</span>
      </td>
      {avecCahier ? <td className={cellule}>{demande.cahier.titre}</td> : null}
      <td className={cellule}>
        <EtatDeLaDemande
          cahierClos={cahierClos}
          demande={demande}
          maintenant={maintenant}
          resumeId={resumeId}
        />
        {bouton === null ? null : (
          // `aria-disabled` et non `disabled` pendant l'envoi : un bouton eteint perd le focus,
          // et un refus doit laisser la main au clavier la ou elle etait.
          <Button
            aria-describedby={resumeId}
            aria-disabled={occupe || undefined}
            className="mt-2 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => agir(demande, bouton.action)}
            size="sm"
            variant="outline"
          >
            {bouton.libelle}
          </Button>
        )}
      </td>
    </tr>
  );
}

/** L'etat d'une demande : son badge, son age, et selon le cas l'avancement ou l'erreur. */
function EtatDeLaDemande({
  demande,
  maintenant,
  resumeId,
  cahierClos,
}: {
  demande: Demande;
  maintenant: number;
  resumeId: string;
  cahierClos: boolean;
}) {
  const badge = (
    <span className={cn(BADGE, TON_ETAT[demande.etat])}>{LIBELLE_ETAT_DEMANDE[demande.etat]}</span>
  );
  const petit = "text-xs text-muted-foreground";

  if (demande.etat === "en_attente") {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {badge}
        <span className={petit}>depuis {depuis(demande.cree_le, maintenant)}</span>
      </span>
    );
  }

  if (demande.etat === "en_cours") {
    // Un montage et une demande `ecrire` (Claude, #273) ne rendent pas de versions : pas de
    // barre d'avancement, qui resterait a « 0 sur 0 ».
    if (demande.type === "monter" || demande.type === "ecrire") {
      return (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {badge}
          <span className={petit}>
            {demande.type === "monter" ? "montage du PDF sur le PC" : "Claude écrit, sur le PC"}
          </span>
        </span>
      );
    }
    const part = demande.total === 0 ? 0 : Math.round((demande.faites / demande.total) * 100);
    const avancement = `${demande.faites} sur ${demande.total}`;
    return (
      <>
        <span className="flex items-center justify-between gap-2">
          {badge}
          <span className={cn(petit, "tabular-nums")}>{avancement}</span>
        </span>
        <div
          aria-labelledby={resumeId}
          aria-valuemax={demande.total}
          aria-valuemin={0}
          aria-valuenow={demande.faites}
          aria-valuetext={avancement}
          className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted"
          role="progressbar"
        >
          <div className="h-full bg-foreground" style={{ width: `${part}%` }} />
        </div>
      </>
    );
  }

  // Faite, echouee ou annulee : l'age compte depuis la fin.
  const age = `il y a ${depuis(demande.finie_le ?? demande.cree_le, maintenant)}`;
  const reprise = demande.type === "rendre" ? repriseEnClair(demande) : null;
  return (
    <>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {badge}
        <span className={petit}>
          {demande.etat === "echouee" && demande.type === "rendre"
            ? `après ${demande.faites} ${demande.faites > 1 ? "versions" : "version"} sur ${demande.total} · ${age}`
            : age}
        </span>
      </span>
      {demande.etat === "echouee" && demande.erreur !== null ? (
        <p className="mt-1.5 whitespace-pre-wrap break-words font-mono text-xs text-jugement-jeter">
          {demande.erreur}
        </p>
      ) : null}
      {demande.etat === "faite" ? null : cahierClos ? (
        <p className={cn(petit, "mt-1")}>le cahier est clos : elle ne se relance plus</p>
      ) : reprise !== null ? (
        <p className={cn(petit, "mt-1")}>{reprise}</p>
      ) : null}
    </>
  );
}

/**
 * La carte des demandes de l'accueil : celles qui attendent ou tournent, puis les dernieres
 * finies. Toujours a l'ecran, meme vide : c'est elle qui porte le message d'une ecriture.
 */
export function CarteDemandes({
  demandes,
  maintenant,
  cahiersClos,
}: {
  demandes: readonly Demande[];
  maintenant: number;
  /** Les cahiers clos de l'accueil : leurs demandes arretees ne se relancent plus. */
  cahiersClos?: ReadonlySet<number> | undefined;
}) {
  const [retour, setRetour] = useState<Retour | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id="coloriage-demandes">
          Demandes
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {demandes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucune demande en cours ni récente. Coche des pages dans un cahier pour les relancer, ou
            monte son PDF.
          </p>
        ) : (
          <TableDemandes
            annoncer={setRetour}
            avecCahier
            cahiersClos={cahiersClos}
            demandes={demandes}
            maintenant={maintenant}
            titreId="coloriage-demandes"
          />
        )}
        <MessageDeRetour retour={retour} />
      </CardContent>
    </Card>
  );
}

/** Les demandes qui tournent sur un cahier, au-dessus de sa grille. Rien quand il n'y en a pas. */
export function DemandesDuCahier({
  demandes,
  maintenant,
  annoncer,
}: {
  demandes: readonly Demande[];
  maintenant: number;
  annoncer: (retour: Retour) => void;
}) {
  if (demandes.length === 0) return null;
  return (
    <section
      aria-labelledby="coloriage-demandes-cahier"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="text-base font-semibold" id="coloriage-demandes-cahier">
        Demandes en cours
      </h2>
      <TableDemandes
        annoncer={annoncer}
        avecCahier={false}
        demandes={demandes}
        maintenant={maintenant}
        titreId="coloriage-demandes-cahier"
      />
    </section>
  );
}
